import { useTranslation } from "react-i18next";
import { IconBell, IconLoader2 } from "@tabler/icons-react";
import { Button } from "../../../components/ui/Button";
import {
  useNotificationOptIn,
  type PushOptInOutcome,
} from "../../../domains/notification/hooks/useNotificationOptIn";

/**
 * Full-page push-notification opt-in UI (header, bell, primary + dismiss
 * buttons). The opt-in behaviour lives in `useNotificationOptIn`; this owns only
 * the presentation. `onDone` is the caller's completion action.
 *
 * Layout is intentionally container-less: the caller wraps it in its own flex
 * column (an embedded onboarding step vs a full-page interstitial).
 */
export function NotificationPrompt({
  onDone,
}: {
  onDone: (outcome: PushOptInOutcome) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { isRequesting, showOpenSettings, enable, openSettings, maybeLater } =
    useNotificationOptIn(onDone);

  return (
    <>
      <div className="flex flex-col gap-3 w-full">
        <h1 className="font-['DM_Sans',sans-serif] font-bold text-[30px]/8 tracking-[-1px] text-[#2c2c2c]">
          {t("Stay current with science")}
        </h1>
        <p className="font-['DM_Sans',sans-serif] text-[15px]/6 tracking-[-0.15px] text-[#929292]">
          {showOpenSettings
            ? t(
                "Notifications are currently disabled. Open settings to turn them back on."
              )
            : t(
                "We'll notify you only when impactful articles in your field are published"
              )}
        </p>
      </div>

      <div className="flex flex-1 items-center justify-center w-full">
        <div className="flex items-center justify-center size-24 rounded-full bg-[#f2f2f2]">
          <IconBell size={48} color="#929292" />
        </div>
      </div>

      <div className="flex flex-col items-center gap-3 w-full pb-2">
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          disabled={isRequesting}
          onClick={showOpenSettings ? openSettings : enable}
        >
          {isRequesting ? (
            <IconLoader2 size={16} className="animate-spin" />
          ) : showOpenSettings ? (
            t("Open settings")
          ) : (
            t("Turn on Notifications")
          )}
        </Button>
        <Button
          variant="ghost"
          className="w-full"
          data-testid="dismiss-modal"
          disabled={isRequesting}
          onClick={maybeLater}
        >
          {t("Maybe later")}
        </Button>
      </div>
    </>
  );
}
